import React, { Fragment, useMemo, useState } from 'react';
import ReactTable from "react-table";
import {
    Card,
    CardBody,
    Pagination,
    PaginationItem,
    PaginationLink,
    Tooltip,
} from 'reactstrap';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import { AiOutlineInfoCircle } from 'react-icons/ai';

/* ---------- COMMON CELL STYLE (IMPORTANT) ---------- */
const cellStyle = {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    
};

/* ---------------- Pagination ---------------- */

const ReservationPagination = ({
    page,
    totalPages,
    onPageChange,
    pagesPerChunk,
}) => {
    const chunkSize = Math.max(1, pagesPerChunk || 1);
    const chunkStart = Math.floor(page / chunkSize) * chunkSize;
    const chunkEnd = Math.min(chunkStart + chunkSize, totalPages);
    const canPrevious = page > 0;
    const canNext = totalPages ? page < totalPages - 1 : false;

    const renderPageButtons = () => {
        const items = [];
        for (let i = chunkStart; i < chunkEnd; i += 1) {
            items.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onPageChange(i);
                        }}
                    >
                        {i + 1}
                    </PaginationLink>
                </PaginationItem>
            );
        }
        return items;
    };

    return (
        <div className="d-flex flex-column align-items-center mt-3">
            <Pagination size="sm" className="mb-2">
                <PaginationItem disabled={!canPrevious}>
                    <PaginationLink
                        href="#"
                        onClick={() => canPrevious && onPageChange(page - 1)}
                    >
                        <i className="simple-icon-arrow-left" />
                    </PaginationLink>
                </PaginationItem>

                {renderPageButtons()}

                <PaginationItem disabled={!canNext}>
                    <PaginationLink
                        href="#"
                        onClick={() => canNext && onPageChange(page + 1)}
                    >
                        <i className="simple-icon-arrow-right" />
                    </PaginationLink>
                </PaginationItem>
            </Pagination>

            <span className="text-muted text-small">
                Page {totalPages ? page + 1 : 0} of {totalPages || 0}
            </span>
        </div>
    );
};

/* ---------------- Main Component ---------------- */

export default function Logs({
    data = [],
    loading,
    onRefresh,
    page = 0,
    pageSize = 10,
    totalPages = 0,
    totalItems = 0,
    pagesPerChunk = 5,
    onPageChange = () => {},
}) {

    const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(() => [
        {
            Header: "Batch Number",
            accessor: "batch",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value}</p>,
            minWidth: 140,
        },
        {
            Header: "Date Time",
            accessor: "dateTime",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value}</p>,
            minWidth: 140,
        },
        {
            Header: "Reservation Id",
            accessor: "reservation",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value}</p>,
            minWidth: 140,
        },
        {
            Header: "Phone",
            accessor: "phone",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value}</p>,
            minWidth: 140,
        },
        {
            Header: "Email",
            accessor: "email",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value || '-'}</p>,
        },
        {
            Header: "Trigger",
            accessor: "trigger",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value || '-'}</p>,
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: (props) => {
                const [tooltipOpen, setTooltipOpen] = useState(false);
                const isFailed =
                    props.value && props.value.toLowerCase() === 'failed';

                const tooltipId = `fail-info-${props.index}`;

                return (
                    <div className="d-flex align-items-start">
                        <p className="mb-0" style={cellStyle}>
                            {props.value || '-'}
                        </p>

                        {isFailed && (
                            <>
                                <span
                                    id={tooltipId}
                                    style={{ marginLeft: 6, cursor: 'pointer' }}
                                >
                                    <AiOutlineInfoCircle size={15} />
                                </span>

                                <Tooltip
                                    placement="top"
                                    isOpen={tooltipOpen}
                                    target={tooltipId}
                                    toggle={() => setTooltipOpen(!tooltipOpen)}
                                >
                                    {props.original?.failureReason ||
                                        'Failure reason not available'}
                                </Tooltip>
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            Header: "Channel",
            accessor: "channel",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value || '-'}</p>,
        },
        {
            Header: "Message",
            accessor: "message",
            Cell: ({ value }) => <p className="mb-0" style={cellStyle}>{value || '-'}</p>,
            minWidth: 220,
        },
    ], [onRefresh]);

    if (loading) {
        return (
            <Card className="d-flex flex-row mb-3">
                <CardBody>
                    <h4 className="text-center">Loading...</h4>
                </CardBody>
            </Card>
        );
    }

    return (
        <Fragment>
            <Colxx xxs="12" className="mb-2">
                <Card className="mb-2">
                    <CardBody>
                        <ReactTable
                            data={rows}
                            columns={columns}
                            defaultPageSize={pageSize}
                            showPagination={false}
                            filterable={false}
                            className="-striped -highlight -bordered"
                            style={{ tableLayout: 'fixed' }}  
                            minRows={rows.length ? undefined : pageSize}
                            noDataText="No logs found"
                        />

                        <ReservationPagination
                            page={page}
                            totalPages={totalPages}
                            pagesPerChunk={pagesPerChunk}
                            onPageChange={onPageChange}
                        />

                        <div className="text-muted text-right mt-2">
                            Showing page {totalPages ? page + 1 : 0} of {totalPages || 0} ({totalItems} total)
                        </div>
                    </CardBody>
                </Card>
            </Colxx>
        </Fragment>
    );
}
